import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { getScrollParent, smoothScroll } from '@src/public/utils';
import { getGuid } from '@src/public/utils/app';
import { formatCommentTime } from '@src/public/utils/format';
import { dialog, message as toast } from '@src/mobile/components/ModalBox';
import Link from '@src/mobile/components/Link';
import { getCommentChildren, postDeleteComment } from '../../services';
import { Store } from '../../store';
import styles from './index.css';
import { addReplyUser, replyComment } from '../../utils';
import {
    POSITION_MAIN,
    POSITION_SUB_TOP,
    REFRESH_SUB_COMMENTS_TYPE_DEL,
    REFRESH_SUB_COMMENTS_TYPE_INSERT,
} from '../../constants';

const Footer = ({ content }) => {
    const {
        updateTime,
        createTime,
        hideExtra,
        commentUserId,
        subjectId,
        commentId,
        comment,
        userName,
        rootId,
        position,
        allowComment,
    } = content;
    const appGuid = getGuid();
    const store = Store.useContainer();
    const { states, changeStates } = store;
    const { list, replyList, docMeta } = states;

    const wrapRef = useRef();

    const refreshSubComments = async ({ comments, rootId, type, commentItem, commentId }) => {
        const index = comments.findIndex(item => item.id === rootId);

        if (index !== -1) {
            const item = comments[index];

            if (type === REFRESH_SUB_COMMENTS_TYPE_INSERT) {
                // 如果是在一级评论页面插入二级评论（预览最多 2 条）
                let { comments: list, commentCount: total } = item;

                list = [commentItem, ...list].slice(0, 2);
                total += 1;

                addReplyUser(list);

                comments[index] = {
                    ...item,
                    comments: list,
                    commentCount: total,
                };
            } else if (type === REFRESH_SUB_COMMENTS_TYPE_DEL) {
                // 如果是在一级评论页面删除二级评论
                let { comments: list, commentCount: total } = item;

                if (total <= 2) {
                    // 如果总回复数小于等于预览条数，直接删除
                    list = list.filter(item => item.id !== commentId).slice(0, 2);
                } else {
                    // 总回复数大于预览条数时，用子评论接口补齐预览
                    const res = await getCommentChildren({
                        subjectId,
                        commentId: rootId,
                        page: 1,
                        pageSize: 10,
                        order: 'asc',
                        docUrl: docMeta?.docUrl,
                    });

                    if (res && res.status && res.data) {
                        const { code, data } = res.data;

                        if (code === 200) {
                            let { list: resList = [] } = data;

                            // 接口返回的列表基于现在的列表去重
                            resList = resList.filter(resItem => !list.find(item => item.id === resItem.id));
                            list = [...list, ...resList].filter(item => item.id !== commentId).slice(0, 2);
                        }
                    }
                }

                total = Math.max(total - 1, 0);

                addReplyUser(list);

                comments[index] = {
                    ...item,
                    comments: list,
                    commentCount: total,
                };
            }
        }
    };

    const insertComment = async insertItem => {
        const { rootId } = insertItem;
        const newList = [...list];

        if (rootId !== '0') {
            if (position === POSITION_MAIN) {
                await refreshSubComments({
                    comments: newList,
                    rootId,
                    commentItem: insertItem,
                    type: REFRESH_SUB_COMMENTS_TYPE_INSERT,
                });

                changeStates({ list: newList });
            } else {
                // 本地插入
                const newReplyList = [insertItem, ...replyList];

                addReplyUser(newReplyList);

                changeStates({ replyList: newReplyList });

                smoothScroll(getScrollParent(wrapRef.current), 0);
            }
        } else {
            newList.unshift(insertItem);

            changeStates({ list: newList });

            smoothScroll(getScrollParent(wrapRef.current), 0);
        }
    };

    const handleReplyClick = () => {
        const replyRootId = rootId === '0' ? commentId : rootId;

        replyComment({
            replyName: userName,
            subjectId,
            rootId: replyRootId,
            pId: commentId,
            docMeta,
            callback: insertComment,
        });
    };

    const handleDeleteClick = () => {
        dialog({
            content: <div style={{ textAlign: 'center' }}>是否删除此评论</div>,
            onOk: async () => {
                try {
                    const res = await postDeleteComment({ commentId });
                    // jsonp 直接返回业务数据；成功码为 1
                    const result = res?.data && res.status !== undefined ? res.data : res;

                    if (result && result.code === 1) {
                        toast({ content: '删除成功' });
                        let newList = [...list];

                        // 本地删除评论
                        if (rootId !== '0') {
                            // 删除二级评论
                            if (position === POSITION_MAIN) {
                                await refreshSubComments({
                                    comments: newList,
                                    rootId,
                                    commentId,
                                    type: REFRESH_SUB_COMMENTS_TYPE_DEL,
                                });
                            } else {
                                // 本地删除
                                const newReplyList = replyList.filter(item => item.id !== commentId);

                                changeStates({ replyList: newReplyList });
                            }
                        } else {
                            // 删除一级评论
                            newList = list.filter(item => item.id !== commentId);
                        }

                        changeStates({ list: newList });
                    } else {
                        toast({ content: result?.msg || '删除失败' });
                    }
                } catch (e) {
                    console.error(e);
                }
            },
            okText: '确认',
            cancelText: '取消',
        });
    };

    const renderReportLink = () => {
        const deviceInfo = window.grounds?.getDeviceInfo();
        const av = deviceInfo?.getAv();
        const gv = deviceInfo?.getGv();
        const proid = deviceInfo?.getProid();
        const os = deviceInfo?.getOs();
        const screen = deviceInfo?.getScreen();
        const uid = deviceInfo?.getUid();
        const publishid = deviceInfo?.getPublishid();
        const st = new Date().getTime();

        return `https://client.ifeng.com/report/comment?type=timeline&av=${av}&gv=${gv}&proid=${proid}&os=${os}&screen=${screen}&uid=${uid}&publishid=${publishid}&st=${st}&commentId=${commentId}&commentContent=${encodeURIComponent(
            comment,
        )}&docId=${subjectId}`;
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <div className={styles.left}>
                <span className={styles.time}>{formatCommentTime(updateTime || createTime)}</span>
                {allowComment ? (
                    <span className={styles.reply} onClick={handleReplyClick}>
                        回复
                    </span>
                ) : null}
            </div>
            <div className={styles.right}>
                {!hideExtra && position !== POSITION_SUB_TOP ? (
                    <>
                        {appGuid === commentUserId ? (
                            <div className={styles.delete} onClick={handleDeleteClick}>
                                删除
                            </div>
                        ) : (
                            <Link to={renderReportLink()}>
                                <div className={styles.report} />
                            </Link>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
};

Footer.propTypes = {
    content: PropTypes.object,
};

export default errorBoundary(Footer);
